import { Badge } from "@thilke/ui/components/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@thilke/ui/components/command";
import { cn } from "@thilke/ui/lib/utils";
import type * as React from "react";

export interface CommandAction {
  id: string;
  label: string;
  group?: string;
  shortcut?: string;
  detail?: string;
  onSelect?: () => void;
}

export interface CommandPaletteProps
  extends React.ComponentProps<typeof Command> {
  actions: CommandAction[];
  placeholder?: string;
  emptyLabel?: string;
}

function CommandPalette({
  actions,
  placeholder = "Search actors, items, rules, macros...",
  emptyLabel = "No matching actions.",
  className,
  ...props
}: CommandPaletteProps) {
  const grouped = actions.reduce<Record<string, CommandAction[]>>(
    (acc, action) => {
      const group = action.group ?? "Actions";
      acc[group] ??= [];
      acc[group].push(action);
      return acc;
    },
    {},
  );

  return (
    <Command
      className={cn("rounded-md border border-border bg-card", className)}
      {...props}
    >
      <CommandInput placeholder={placeholder} />
      <CommandList>
        <CommandEmpty>{emptyLabel}</CommandEmpty>
        {Object.entries(grouped).map(([group, groupActions]) => (
          <CommandGroup key={group} heading={group}>
            {groupActions.map((action) => (
              <CommandItem
                key={action.id}
                value={action.label}
                onSelect={action.onSelect}
              >
                <span className="min-w-0 flex-1 truncate">{action.label}</span>
                {action.detail ? (
                  <Badge
                    variant="secondary"
                    className="font-normal normal-case"
                  >
                    {action.detail}
                  </Badge>
                ) : null}
                {action.shortcut ? (
                  <CommandShortcut>{action.shortcut}</CommandShortcut>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  );
}

export { CommandPalette };
